import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

type Shop = 'amazon' | 'mediamarkt';

interface ProductWalkthroughSourceProps {
  onGenerate: (input: { source_url: string; shop: Shop }) => Promise<void>;
  onBack: () => void;
  isLoading?: boolean;
  externalError?: string | null;
}

const SHOP_OPTIONS: { value: Shop; label: string; placeholder: string }[] = [
  { value: 'amazon', label: 'Amazon', placeholder: 'https://www.amazon.de/dp/B0XXXXXXXX' },
  { value: 'mediamarkt', label: 'Media Markt', placeholder: 'https://www.mediamarkt.de/de/product/_example-1234567.html' }
];

export const ProductWalkthroughSource: React.FC<ProductWalkthroughSourceProps> = ({
  onGenerate,
  onBack,
  isLoading = false,
  externalError = null
}) => {
  const [shop, setShop] = useState<Shop>('amazon');
  const [sourceUrl, setSourceUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const selectedShop = SHOP_OPTIONS.find((option) => option.value === shop) ?? SHOP_OPTIONS[0];

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const cleanUrl = sourceUrl.trim();
    if (!cleanUrl) {
      setError(`Enter a ${selectedShop.label} product page URL.`);
      return;
    }
    setError(null);
    await onGenerate({ source_url: cleanUrl, shop });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            Product Walkthrough (Shelf)
          </h2>
          <p className="text-gray-600">
            Paste a product page URL. SIMPI will generate the five-step shelf walkthrough and stop on the review page.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="product-shop" className="block text-sm font-medium text-gray-700 mb-2">
              Online shop
            </label>
            <select
              id="product-shop"
              value={shop}
              onChange={(event) => setShop(event.target.value as Shop)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              disabled={isLoading}
            >
              {SHOP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="product-url" className="block text-sm font-medium text-gray-700 mb-2">
              {selectedShop.label} product page URL
            </label>
            <input
              id="product-url"
              type="url"
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              placeholder={selectedShop.placeholder}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              The local store version (e.g. .de, .com, .es) is detected automatically from the URL.
            </p>
          </div>

          {(error || externalError) && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error || externalError}
            </div>
          )}

          <div className="flex justify-between gap-3 pt-4">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Back
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-5 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Generate walkthrough
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
